import html2pdf from 'html2pdf.js';

const EXPORT_IGNORE_SELECTOR = '[data-export-ignore]';
const OVERFLOW_CONTAINER_SELECTOR = [
  '[class*="measurement-table-section"]',
  '[class*="-charts-scroll"]',
  '[class*="table-wrap"]',
  '[class*="chart-area"]'
].join(', ');

const EXPORT_PRINT_STYLES = `
  [data-export-ignore] { display: none !important; }
  [class*="measurement-table-section"],
  [class*="-charts-scroll"],
  [class*="table-wrap"],
  [class*="chart-area"] {
    overflow: visible !important;
    overflow-x: visible !important;
    overflow-y: visible !important;
    max-height: none !important;
  }
  table {
    width: max-content !important;
    min-width: 100% !important;
  }
`;

function expandOverflowContainers(root) {
  const restores = new Map();

  const remember = (el) => {
    if (restores.has(el)) return;
    restores.set(el, {
      overflow: el.style.overflow,
      overflowX: el.style.overflowX,
      overflowY: el.style.overflowY,
      maxHeight: el.style.maxHeight,
      maxWidth: el.style.maxWidth,
      width: el.style.width,
      minWidth: el.style.minWidth
    });
  };

  root.querySelectorAll('*').forEach((el) => {
    const style = window.getComputedStyle(el);
    const hasHorizontalOverflow = style.overflowX === 'auto' || style.overflowX === 'scroll';
    const hasVerticalOverflow = style.overflowY === 'auto' || style.overflowY === 'scroll';

    if (hasHorizontalOverflow || hasVerticalOverflow) {
      remember(el);
      el.style.overflow = 'visible';
      el.style.overflowX = 'visible';
      el.style.overflowY = 'visible';
      el.style.maxHeight = 'none';
    }
  });

  root.querySelectorAll('table').forEach((table) => {
    remember(table);
    table.style.width = 'max-content';
    table.style.minWidth = '100%';
  });

  remember(root);
  const fullWidth = Math.max(root.scrollWidth, root.offsetWidth, root.clientWidth);
  if (fullWidth > root.clientWidth) {
    root.style.width = `${fullWidth}px`;
    root.style.maxWidth = 'none';
  }

  return () => {
    restores.forEach((styles, el) => {
      el.style.overflow = styles.overflow;
      el.style.overflowX = styles.overflowX;
      el.style.overflowY = styles.overflowY;
      el.style.maxHeight = styles.maxHeight;
      el.style.maxWidth = styles.maxWidth;
      el.style.width = styles.width;
      el.style.minWidth = styles.minWidth;
    });
  };
}

function prepareExportClone(clonedDoc, rootId) {
  const root = rootId ? clonedDoc.getElementById(rootId) : clonedDoc.body;
  if (!root) return;

  root.querySelectorAll(EXPORT_IGNORE_SELECTOR).forEach((el) => {
    el.style.display = 'none';
  });

  root.querySelectorAll('.bp-section--controls').forEach((el) => {
    el.style.display = 'none';
  });

  root.querySelectorAll('.bp-print-only-header').forEach((el) => {
    el.style.display = 'block';
    el.style.marginBottom = '20px';
    el.style.padding = '16px 20px';
    el.style.background = '#f8fafc';
    el.style.border = '1px solid #cbd5e1';
    el.style.borderRadius = '8px';
  });

  root.querySelectorAll('.bp-print-info-grid').forEach((el) => {
    el.style.display = 'flex';
    el.style.flexWrap = 'wrap';
    el.style.gap = '10px 16px';
  });

  root.querySelectorAll('.bp-print-info-item').forEach((el) => {
    el.style.width = '30%';
    el.style.boxSizing = 'border-box';
  });

  root.querySelectorAll('.pc-print-only').forEach((el) => {
    el.style.display = 'block';
  });

  root.querySelectorAll('.pc-print-item').forEach((el) => {
    el.style.display = 'block';
    el.style.borderBottom = '1px dashed #cbd5e1';
    el.style.paddingBottom = '3px';
    el.style.marginBottom = '3px';
  });

  root.querySelectorAll('.pc-screen-only').forEach((el) => {
    el.style.display = 'none';
  });

  [
    ['.prc-status-label.is-pending', 'P', '#d97706'],
    ['.prc-status-label.is-reviewed', 'R', '#15803d']
  ].forEach(([selector, label, color]) => {
    root.querySelectorAll(selector).forEach((el) => {
      el.textContent = label;
      el.style.setProperty('min-width', '0', 'important');
      el.style.setProperty('margin', '0', 'important');
      el.style.setProperty('padding', '0', 'important');
      el.style.setProperty('border', 'none', 'important');
      el.style.setProperty('border-radius', '0', 'important');
      el.style.setProperty('background', 'transparent', 'important');
      el.style.setProperty('color', color, 'important');
      el.style.setProperty('font-size', '9px', 'important');
      el.style.setProperty('line-height', '1', 'important');
      el.style.setProperty('font-weight', '700', 'important');
    });
  });

  root.querySelectorAll('*').forEach((el) => {
    const style = clonedDoc.defaultView.getComputedStyle(el);
    const hasHorizontalOverflow = style.overflowX === 'auto' || style.overflowX === 'scroll';
    const hasVerticalOverflow = style.overflowY === 'auto' || style.overflowY === 'scroll';

    if (hasHorizontalOverflow || hasVerticalOverflow) {
      el.style.overflow = 'visible';
      el.style.overflowX = 'visible';
      el.style.overflowY = 'visible';
      el.style.maxHeight = 'none';
    }
  });

  root.querySelectorAll(OVERFLOW_CONTAINER_SELECTOR).forEach((el) => {
    el.style.overflow = 'visible';
    el.style.overflowX = 'visible';
    el.style.overflowY = 'visible';
    el.style.maxHeight = 'none';
  });

  root.querySelectorAll('table').forEach((table) => {
    table.style.width = 'max-content';
    table.style.minWidth = '100%';
  });

  const width = Math.max(root.scrollWidth, root.offsetWidth);
  if (width > 0) {
    root.style.width = `${width}px`;
    root.style.maxWidth = 'none';
  }
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function exportElementToPdf(element, filename, options = {}) {
  if (!element) throw new Error('Export element not found');

  const elementId = element.id || null;
  const userOnClone = options.html2canvas?.onclone;
  const restoreExpanded = expandOverflowContainers(element);
  const captureWidth = Math.max(element.scrollWidth, element.offsetWidth, element.clientWidth);

  try {
    const html2canvasOpts = {
      scale: 2,
      useCORS: true,
      logging: false,
      scrollX: 0,
      scrollY: -window.scrollY,
      windowWidth: captureWidth,
      width: captureWidth,
      ...(options.html2canvas || {}),
      onclone: (clonedDoc) => {
        prepareExportClone(clonedDoc, elementId);
        userOnClone?.(clonedDoc);
      }
    };

    const opt = {
      margin: [0.2, 0.2, 0.2, 0.2],
      filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: html2canvasOpts,
      jsPDF: { unit: 'in', format: 'letter', orientation: 'landscape' },
      pagebreak: { mode: ['css', 'legacy'] },
      ...options,
      html2canvas: html2canvasOpts
    };

    await html2pdf().set(opt).from(element).save();
  } finally {
    restoreExpanded();
  }
}

export function printElement(element, title = 'Report') {
  if (!element) throw new Error('Print element not found');

  const w = window.open('', '_blank');
  if (!w) throw new Error('Popup blocked');

  const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
    .map((node) => node.outerHTML)
    .join('\n');

  w.document.open();
  w.document.write(`
    <!doctype html>
    <html>
      <head>
        <title>${title}</title>
        ${styles}
        <style>
          ${EXPORT_PRINT_STYLES}
          @media print {
            body { background: #fff !important; }
            .btn-back-shared { display: none !important; }
            .bp-section--controls { display: none !important; }
          }
          .rpm-print-only-header {
            display: block !important;
            margin-bottom: 16px;
            padding: 14px 18px;
            background: #f8fafc;
            border: 1px solid #cbd5e1;
            border-radius: 8px;
            font-family: inherit;
          }
          .bp-print-only-header {
            display: block !important;
            margin-bottom: 16px;
          }
          .bp-section--controls { display: none !important; }

          @page {
            size: landscape;
            margin: 8mm;
          }
          #rpm-overview-print-area {
            width: 100% !important;
            max-width: none !important;
            color: #0f2942;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          #rpm-overview-print-area .rpm-print-only-header {
            background: #f8fafc !important;
            border-color: #b8c9d9 !important;
          }
          #rpm-overview-print-area .bphr-grid-container {
            width: 100% !important;
            max-width: none !important;
            overflow: visible !important;
          }
          #rpm-overview-print-area .bphr-table {
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
            table-layout: fixed !important;
            font-size: 7px !important;
          }
          #rpm-overview-print-area .bphr-table col {
            width: 4.4% !important;
          }
          #rpm-overview-print-area .bphr-table col:first-child {
            width: 7.6% !important;
          }
          #rpm-overview-print-area .bphr-table thead {
            display: table-header-group;
          }
          #rpm-overview-print-area .bphr-table tr {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          #rpm-overview-print-area .bphr-table .datehd,
          #rpm-overview-print-area .bphr-table .grp,
          #rpm-overview-print-area .bphr-table .sub {
            padding: 4px 1px !important;
            font-size: 6.5px !important;
            white-space: normal !important;
            overflow-wrap: anywhere;
          }
          #rpm-overview-print-area .bphr-table .hds {
            font-size: 5.5px !important;
            white-space: normal !important;
          }
          #rpm-overview-print-area .bphr-table td.cell,
          #rpm-overview-print-area .bphr-table td.cell.metric,
          #rpm-overview-print-area .bphr-table td.bphr-status-cell {
            width: auto !important;
            padding: 4px 1px !important;
          }
          #rpm-overview-print-area .bphr-table td.cell.date-col {
            font-size: 7px !important;
            white-space: normal !important;
          }
          #rpm-overview-print-area .bphr-metric {
            gap: 1px !important;
            padding: 0 !important;
          }
          #rpm-overview-print-area .bphr-metric-value {
            min-height: 0 !important;
            font-size: 8px !important;
          }
          #rpm-overview-print-area .bphr-metric-label {
            min-height: 0 !important;
            font-size: 6px !important;
            white-space: normal !important;
          }
          #rpm-overview-print-area .pd-reading-review {
            gap: 1px !important;
            padding: 1px 2px !important;
            font-size: 5.5px !important;
            white-space: normal !important;
          }
          #rpm-overview-print-area .prc-status-label {
            min-width: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            border-radius: 0 !important;
            background: transparent !important;
            font-size: 0 !important;
            line-height: 1 !important;
          }
          #rpm-overview-print-area .prc-status-label::after {
            display: inline;
            font-size: 9px;
            font-weight: 700;
          }
          #rpm-overview-print-area .prc-status-label.is-pending::after {
            content: "P";
            color: #d97706;
          }
          #rpm-overview-print-area .prc-status-label.is-reviewed::after {
            content: "R";
            color: #15803d;
          }
          #rpm-overview-print-area .bphr-table svg {
            max-width: 8px !important;
            max-height: 8px !important;
          }
          #rpm-overview-print-area .pdx-panel,
          #rpm-overview-print-area .pdx-kpi {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          #rpm-overview-print-area .recharts-responsive-container,
          #rpm-overview-print-area .recharts-wrapper,
          #rpm-overview-print-area .recharts-surface {
            max-width: 100% !important;
          }
        </style>
      </head>
      <body>
        ${element.outerHTML}
      </body>
    </html>
  `);
  w.document.close();
  w.focus();
  setTimeout(() => {
    w.print();
    w.close();
  }, 250);
}

export function toCsv(rows) {
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return rows.map((r) => r.map(escape).join(',')).join('\n');
}
